import { Box, Skeleton, Typography, useTheme } from '@mui/material';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';

import { HomeMiniTrackMap } from '../home/HomeMiniTrackMap';
import { useCircuitMapPreviewPoints } from '../../hooks/useCircuitMapPreviewPoints';
import type { CircuitListItem } from '../../lib/circuits';

type CircuitTrackPreviewProps = {
  circuit: CircuitListItem;
  height?: number;
};

export function CircuitTrackPreview({ circuit, height = 148 }: CircuitTrackPreviewProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { containerRef, points, loading } = useCircuitMapPreviewPoints(circuit);

  if (loading) {
    return (
      <Box ref={containerRef} sx={{ width: '100%' }}>
        <Skeleton
          variant="rounded"
          height={height}
          animation="wave"
          sx={{
            width: '100%',
            borderRadius: 1.25,
            bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'subtleFill.light',
          }}
        />
      </Box>
    );
  }

  if (points.length < 2) {
    return (
      <Box
        ref={containerRef}
        sx={{
          height,
          width: '100%',
          borderRadius: 1.25,
          overflow: 'hidden',
          bgcolor: 'subtleFill.light',
          border: '1px solid',
          borderColor: 'divider',
          display: 'grid',
          placeItems: 'center',
          color: 'text.secondary',
        }}
      >
        <Box sx={{ textAlign: 'center', px: 1.5 }}>
          <MapOutlinedIcon sx={{ fontSize: 28, opacity: 0.55, mb: 0.5 }} />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
            {circuit.source === 'catalog' ? 'Catalog baseline' : 'No track outline yet'}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box ref={containerRef} sx={{ width: '100%' }}>
      <HomeMiniTrackMap points={points} height={height} strokeWidth={2.6} />
    </Box>
  );
}
